// Card ordering and filtering for the Realtime grid. Pure functions, so the
// drag-and-drop maths can be tested without a browser.

interface Orderable {
    deviceId: string;
    order?: number | null;
}

/**
 * Sorts cards by their saved `order`, keeping the API's order for ties; cards
 * without one go last, by device id. Then renumbers them 1..n.
 */
export function normalizeRecords<T extends Orderable>(records: T[]): (T & { order: number })[] {
    const annotated = records.map((record, index) => {
        // Number(null) is 0, so a card with no saved order sorts as order 0 (first,
        // in API order). Only a non-numeric order (undefined, "abc") counts as none.
        const backendOrder = Number(record.order);
        return { record, index, preferredOrder: Number.isFinite(backendOrder) ? backendOrder : null };
    });

    annotated.sort((a, b) => {
        const aHasOrder = a.preferredOrder !== null;
        const bHasOrder = b.preferredOrder !== null;
        if (aHasOrder && bHasOrder) {
            if (a.preferredOrder !== b.preferredOrder)
                return (a.preferredOrder as number) - (b.preferredOrder as number);
            return a.index - b.index;
        }
        if (aHasOrder) return -1;
        if (bHasOrder) return 1;
        return String(a.record.deviceId).localeCompare(String(b.record.deviceId));
    });

    return annotated.map(({ record }, index) => ({ ...record, order: index + 1 }));
}

/**
 * Moves the card at `dragIndex` to `hoverIndex`, where both index into the
 * *visible* list: the members of `groupId`, or every card when it's null. Within
 * a group, the reordered members are merged back into the single global order and
 * non-members keep their slots. Returns the new global order of device ids, or
 * null when the move is a no-op or out of range.
 */
export function moveWithinGroup(
    orderedIds: string[],
    groupMembers: Set<string> | null,
    dragIndex: number,
    hoverIndex: number
): string[] | null {
    const visible = groupMembers ? orderedIds.filter((id) => groupMembers.has(id)) : orderedIds;
    if (
        dragIndex === hoverIndex ||
        dragIndex < 0 ||
        hoverIndex < 0 ||
        dragIndex >= visible.length ||
        hoverIndex >= visible.length
    ) {
        return null;
    }

    const reordered = [...visible];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(hoverIndex, 0, moved);

    if (!groupMembers) return reordered;
    let cursor = 0;
    return orderedIds.map((id) => (groupMembers.has(id) ? reordered[cursor++] : id));
}

/**
 * The positions to save after a drag: cards whose new order differs from what's
 * stored. A never-ordered card is stored as null and counts as changed, or the
 * next poll would sort it back to the front.
 */
export function changedPositions(
    stored: Map<string, unknown>,
    next: Map<string, number>
): { deviceId: string; order: number }[] {
    return [...next]
        .filter(([deviceId, order]) => stored.get(deviceId) !== order)
        .map(([deviceId, order]) => ({ deviceId, order }));
}

/** The cards to show: members of the selected group (if any), matching the search on name or id. */
export function filterVisibleRecords<T extends { deviceId?: string; name?: string; groupIds?: string[] }>(
    records: T[],
    groupId: string | null,
    search: string
): T[] {
    const inGroup = groupId ? records.filter((record) => record.groupIds?.includes(groupId)) : records;
    const term = search.trim().toLowerCase();
    if (!term) return inGroup;
    return inGroup.filter(
        (record) =>
            String(record.name ?? '')
                .toLowerCase()
                .includes(term) ||
            String(record.deviceId ?? '')
                .toLowerCase()
                .includes(term)
    );
}
